import React from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import { duration } from './primitives/tokens';
import { Icon } from './Icon';
import { Pressable } from './primitives/motion';
import { useControllable } from '../../hooks/useControllable';
import styles from './Checkbox.module.css';

// ── CVA ──────────────────────────────────────────────────────────────────────

const checkbox = cva(styles.label);

// ── Checkbox ─────────────────────────────────────────────────────────────────

interface CheckboxProps extends Omit<React.HTMLAttributes<HTMLLabelElement>, 'onChange'> {
  /** Controlled checked state */
  checked?: boolean;
  /** Initial checked (uncontrolled) */
  defaultChecked?: boolean;
  /** Show indeterminate dash */
  indeterminate?: boolean;
  /** Change handler */
  onChange?: (checked: boolean) => void;
  /** Disabled */
  disabled?: boolean;
  /** Value for group usage */
  value?: string;
  /** Render as a round circle instead of square */
  round?: boolean;
  children?: React.ReactNode;
}

/**
 * Checkbox — boolean toggle with optional indeterminate state.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | checked | boolean | — | Controlled state |
 * | defaultChecked | boolean | false | Initial state |
 * | indeterminate | boolean | false | Dash icon instead of check |
 * | onChange | (checked) => void | — | Change handler |
 * | disabled | boolean | false | Disable |
 * | value | string | — | Value for Checkbox.Group |
 *
 * @example
 * <Checkbox onChange={console.log}>Remember me</Checkbox>
 * <Checkbox checked indeterminate>Select all</Checkbox>
 */
export function Checkbox({
  checked: controlledChecked,
  defaultChecked = false,
  indeterminate = false,
  onChange,
  disabled = false,
  value,
  round = false,
  children,
  style,
  className,
  ...props
}: CheckboxProps) {
  const [checked, setChecked] = useControllable(controlledChecked, defaultChecked, onChange);
  const shouldReduce = useReducedMotion() ?? false;

  const isActive = checked || indeterminate;

  return (
    <label
      className={clsx(checkbox({ className }), disabled && styles.disabled)}
      style={style}
      {...props}
    >
      <Pressable effect="ring" disabled={disabled}>
        <span
          className={clsx(styles.box, round && styles.round, isActive && styles.active)}
        >
          <AnimatePresence>
            {checked && !indeterminate && (
              <motion.svg
                key="check"
                viewBox="0 0 16 16"
                width="13"
                height="13"
                className={styles.iconWrapper}
                aria-hidden="true"
                focusable="false"
              >
                <motion.path
                  d="M3 8.5 L7 12 L13 4"
                  stroke="var(--on-primary)"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  exit={{ pathLength: 0, opacity: 0 }}
                  transition={shouldReduce ? { duration: 0 } : { duration: duration.normal / 1000, ease: 'easeOut' }}
                />
              </motion.svg>
            )}
            {indeterminate && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                transition={shouldReduce ? { duration: 0 } : { duration: duration.fast / 1000 }}
                className={styles.iconWrapper}
              >
                <Icon name="remove" size={13} color="var(--on-primary)" />
              </motion.div>
            )}
          </AnimatePresence>
          <input
            type="checkbox"
            checked={checked}
            onChange={() => !disabled && setChecked(!checked)}
            disabled={disabled}
            value={value}
            className={styles.hiddenInput}
          />
        </span>
      </Pressable>
      {children && <span>{children}</span>}
    </label>
  );
}

// ── Checkbox.Group ───────────────────────────────────────────────────────────

interface CheckboxOption {
  label: React.ReactNode;
  value: string;
  disabled?: boolean;
}

interface CheckboxGroupProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** Controlled selected values */
  value?: string[];
  /** Initial values (uncontrolled) */
  defaultValue?: string[];
  /** Options */
  options?: (string | CheckboxOption)[];
  /** Change handler */
  onChange?: (values: string[]) => void;
  /** Disabled */
  disabled?: boolean;
  /** Layout direction */
  direction?: 'horizontal' | 'vertical';
  children?: React.ReactNode;
}

/**
 * Checkbox.Group — manage multiple checkboxes.
 *
 * @example
 * <Checkbox.Group
 *   options={['Apple', 'Banana', 'Cherry']}
 *   defaultValue={['Apple']}
 *   onChange={console.log}
 * />
 */
function CheckboxGroup({
  value: controlledValue,
  defaultValue = [],
  options,
  onChange,
  disabled = false,
  direction = 'horizontal',
  children,
  style,
  className,
  ...props
}: CheckboxGroupProps) {
  const [selected, setSelected] = useControllable(controlledValue, defaultValue, onChange);

  const toggle = (val: string) => {
    const next = selected.includes(val) ? selected.filter((v) => v !== val) : [...selected, val];
    setSelected(next);
  };

  const normalizedOptions = options?.map((opt) => (typeof opt === 'string' ? { label: opt, value: opt } : opt));

  return (
    <div
      className={clsx(
        styles.group,
        direction === 'vertical' ? styles.groupVertical : styles.groupHorizontal,
        className,
      )}
      style={style}
      {...props}
    >
      {normalizedOptions
        ? normalizedOptions.map((opt) => (
            <Checkbox
              key={opt.value}
              checked={selected.includes(opt.value)}
              onChange={() => toggle(opt.value)}
              disabled={disabled || opt.disabled}
              value={opt.value}
            >
              {opt.label}
            </Checkbox>
          ))
        : children}
    </div>
  );
}

Checkbox.Group = CheckboxGroup;
